import React from 'react';
import { PersonalityRate } from "../../types/damage";
import { StatInputs } from "../../types/stat";
import { PokemonDefenceInterface, rankCorrectionEnum } from "../../pokemon/pokemon";
import { MAX_STAT_EFFORT, normalizeEffortValue } from '../../constants/effort';
import StatsInputTableCore, { StatRowConfig, PersonalityKey, PersonalityStatKey } from '../StatsInputTableCore';

interface StatTableInputProps {
  selectedPokemon: PokemonDefenceInterface;
  setSelectedPokemon: React.Dispatch<React.SetStateAction<PokemonDefenceInterface | null>>;
  localStatInputs: StatInputs;
  updateLocalStat: (statName: keyof Pick<StatInputs, "hp_effort" | "attack_effort" | "defense_effort" | "special_attack_effort" | "special_defense_effort" | "speed_effort">, value: number) => void;
  handleStatChange: (statName: keyof Pick<StatInputs, "hp_effort" | "attack_effort" | "defense_effort" | "special_attack_effort" | "special_defense_effort" | "speed_effort">, value: number) => void;
  handlePersonalityChange: (statName: "attack" | "defense" | "special_attack" | "special_defense" | "speed", value: PersonalityRate) => void;
  handleIndividualValueChange: (statName: "hp" | "attack" | "defense" | "special_attack" | "special_defense" | "speed", value: 0 | 31) => void;
}

function StatTableInput({
  selectedPokemon,
  setSelectedPokemon,
  localStatInputs,
  updateLocalStat,
  handleStatChange,
  handlePersonalityChange,
  handleIndividualValueChange
}: StatTableInputProps) {
  const statRows: StatRowConfig[] = [
    { key: 'hp', label: 'H', showPersonality: false, showRank: false },
    { key: 'attack', label: 'A' },
    { key: 'defense', label: 'B' },
    { key: 'special_attack', label: 'C' },
    { key: 'special_defense', label: 'D' },
    { key: 'speed', label: 'S' },
  ];

  const handleEffortSelect = (
    statName: keyof Pick<
      StatInputs,
      'hp_effort' | 'attack_effort' | 'defense_effort' | 'special_attack_effort' | 'special_defense_effort' | 'speed_effort'
    >,
    nextValue: number,
  ) => {
    const finalValue = normalizeEffortValue(nextValue);

    updateLocalStat(statName, finalValue);
    handleStatChange(statName, finalValue);
  };

  const handleRankSelect = (statName: "attack" | "defense" | "special_attack" | "special_defense" | "speed", rank: number) => {
    if (selectedPokemon) {
      setSelectedPokemon({
        ...selectedPokemon,
        rankCorrection: {
          ...selectedPokemon.rankCorrection,
          [statName]: rank as rankCorrectionEnum
        }
      });
    }
  };

  const personalityKeyMap: Record<PersonalityKey, PersonalityStatKey> = {
    attack_personality: 'attack',
    defense_personality: 'defense',
    special_attack_personality: 'special_attack',
    special_defense_personality: 'special_defense',
    speed_personality: 'speed',
  };

  return (
    <StatsInputTableCore
      rows={statRows}
      effortValues={{
        hp_effort: localStatInputs.hp_effort,
        attack_effort: localStatInputs.attack_effort,
        defense_effort: localStatInputs.defense_effort,
        special_attack_effort: localStatInputs.special_attack_effort,
        special_defense_effort: localStatInputs.special_defense_effort,
        speed_effort: localStatInputs.speed_effort,
      }}
      onEffortChange={(statKey, value) => handleEffortSelect(statKey, value)}
      effortMaxValue={MAX_STAT_EFFORT}
      personalityValues={{
        attack_personality: localStatInputs.attack_personality,
        defense_personality: localStatInputs.defense_personality,
        special_attack_personality: localStatInputs.special_attack_personality,
        special_defense_personality: localStatInputs.special_defense_personality,
        speed_personality: localStatInputs.speed_personality,
      }}
      onPersonalityChange={(key, value) => handlePersonalityChange(personalityKeyMap[key], value)}
      enableNatureConstraint
      ivValues={{
        hp: selectedPokemon.individualValue.hp,
        attack: selectedPokemon.individualValue.attack,
        defense: selectedPokemon.individualValue.defense,
        special_attack: selectedPokemon.individualValue.special_attack,
        special_defense: selectedPokemon.individualValue.special_defense,
        speed: selectedPokemon.individualValue.speed,
      }}
      onIvChange={(statKey, value) => handleIndividualValueChange(statKey, value)}
      rankValues={{
        attack: selectedPokemon.rankCorrection.attack,
        defense: selectedPokemon.rankCorrection.defense,
        special_attack: selectedPokemon.rankCorrection.special_attack,
        special_defense: selectedPokemon.rankCorrection.special_defense,
        speed: selectedPokemon.rankCorrection.speed,
      }}
      onRankChange={(statKey, value) => handleRankSelect(statKey, value)}
      variant="sidebar"
      columnWidths={{ stat: 20, iv: 50, effort: 35, personality: 30, rank: 60 }}
      headerLabels={{
        stat: '能力',
        iv: '個体値',
        effort: '努力値',
        personality: '性格',
        rank: 'ランク',
      }}
    />
  );
}

export default StatTableInput;
